/**
 * Behavior hook for the *created* concept-group concepts view — the created
 * counterpart to ``usePreviewConceptsTable``. Owns debounced search, the
 * standard/non-standard filter, paging, and CSV download against
 * ``useConceptGroupConcepts``. Returns the same model shape as the preview hook
 * so both render through the shared ``ConceptsExplorer`` and look + operate
 * identically (OR-6126).
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { serializeConceptsFilter, serializeConceptsSort } from './conceptsGridParams';
import { downloadConceptGroupCsv, useConceptGroupConcepts } from '../api/conceptGroupQueries';
import type { ConceptGroupConcept } from '../api/conceptGroupQueries';
import type { ConceptsTableModel } from '../components/ConceptsExplorer';
import type { PreviewStandardFilter } from '../conceptStandardFilter';

const SEARCH_DEBOUNCE_MS = 350;
export const CONCEPT_GROUP_CONCEPTS_PAGE_SIZE = 50;

// Match the details dialog's default ordering so the two surfaces open the same.
const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'conceptName', sort: 'asc' }];

export const useConceptGroupConceptsTable = ({
  conceptGroupId,
  groupName,
}: {
  conceptGroupId: string | null;
  groupName: string;
}): ConceptsTableModel => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [standardFilter, setStandardFilterState] = useState<PreviewStandardFilter>('all');
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [page, setPage] = useState(0);
  const [downloading, setDownloading] = useState(false);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const conceptsQuery = useConceptGroupConcepts({
    conceptGroupId,
    page: page + 1,
    pageSize: CONCEPT_GROUP_CONCEPTS_PAGE_SIZE,
    search,
    standardFilter,
    sort: serializeConceptsSort(sortModel),
    filter: serializeConceptsFilter(filterModel),
  });

  const concepts = useMemo<ConceptGroupConcept[]>(
    () => conceptsQuery.data?.concepts ?? [],
    [conceptsQuery.data?.concepts]
  );
  const total = conceptsQuery.data?.total ?? 0;

  const setStandardFilter = useCallback((value: PreviewStandardFilter) => {
    setStandardFilterState(value);
    setPage(0);
  }, []);

  // Sort / filter are server-side, so a change must reset to the first page.
  const onSortModelChange = useCallback((next: GridSortModel) => {
    setSortModel(next);
    setPage(0);
  }, []);

  const onFilterModelChange = useCallback((next: GridFilterModel) => {
    setFilterModel(next);
    setPage(0);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!conceptGroupId) return;
    setDownloading(true);
    try {
      await downloadConceptGroupCsv(conceptGroupId, groupName);
    } finally {
      setDownloading(false);
    }
  }, [conceptGroupId, groupName]);

  return {
    searchInput,
    setSearchInput,
    standardFilter,
    setStandardFilter,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    page,
    setPage,
    pageSize: CONCEPT_GROUP_CONCEPTS_PAGE_SIZE,
    concepts,
    total,
    isLoading: conceptsQuery.isLoading,
    isFetching: conceptsQuery.isFetching,
    isError: conceptsQuery.isError,
    downloading,
    handleDownload,
  };
};
